/**
 * チーム別 (PDG / ACE) の戦闘力テーブル。
 *
 * 期待値はシート (月度目標戦闘力 / pt plan / 【PDG】pt plan) を SSOT とする読み取り専用表示。
 * Web からの編集・保存は廃止された。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チーム別の戦闘力 / バックログ実績 / 期待値 (読み取り専用) / 未対応pt (予定pt 合計) テーブル。目標Pt は当月総目標 (capacity) を括弧併記
 * @graph-connects none
 */

import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { MonthlyCombatPower } from '../../../types/combat-power.js';
import {
  formatNum,
  formatPtWithWip,
  formatActualPowerDisplay,
  monthHeaderToYyyymm,
} from './combat-power-utils.js';
import { TwoLineHeaderLabel } from './TwoLineHeaderLabel.js';

/**
 * 現在の JST 月 ("YYYY-MM") を返す。サーバ側の月境界 (JST) と揃える。
 * @graph-connects none
 */
function currentMonthKeyJst(): string {
  const jst = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return `${String(jst.getUTCFullYear())}-${String(jst.getUTCMonth() + 1).padStart(2, '0')}`;
}

interface TeamTableProps {
  teamLabel: string;
  members: MonthlyCombatPower[];
  currentMonthIdx: number;
  /**
   * 担当者名 (小文字 nickName) → メールアドレスの対応表。
   * 引き当てられた担当者名はメンバー詳細ページ (`/members/$email`) へのリンクになる。
   * 未指定 / 引き当て不可の場合はリンクにせずプレーンテキストで表示する。
   */
  nameToEmail?: Map<string, string>;
  /**
   * チーム代表色 (6 桁 HEX, 例 `#ef4444`)。指定すると見出しを代表色の帯で塗る。
   * ACE のサブチーム別表示で各チームを色分けするために使う。未指定は従来の素の見出し。
   */
  accentColor?: string;
  /** 担当者が 0 名のとき表示する空メッセージ (未指定なら空行を出さない) */
  emptyMessage?: string;
}

/** @graph-connects none */
export function TeamTable({
  teamLabel,
  members,
  currentMonthIdx,
  nameToEmail,
  accentColor,
  emptyMessage,
}: TeamTableProps) {
  const { t } = useTranslation();

  const expectedOf = (m: MonthlyCombatPower): number | null =>
    m.months[currentMonthIdx]?.expectedPower ?? null;

  // バックログ実績Pt合計（完了／処理中の分割）
  const totalCompleted = members.reduce((s, m) => {
    const bp = m.months[currentMonthIdx]?.backlogPt;
    if (!bp) return s;
    return s + bp.completed;
  }, 0);
  const totalInProgress = members.reduce((s, m) => {
    const bp = m.months[currentMonthIdx]?.backlogPt;
    if (!bp) return s;
    return s + bp.inProgress;
  }, 0);
  // 未対応pt（予定pt）合計。現在スナップショットのため当月以外は常に 0
  const totalOpen = members.reduce((s, m) => {
    const bp = m.months[currentMonthIdx]?.backlogPt;
    if (!bp) return s;
    return s + bp.open;
  }, 0);
  // 当月総目標 (capacity) 合計。目標Pt 合計の括弧併記用
  const totalCapacity = members.reduce((s, m) => {
    const cm = m.months[currentMonthIdx];
    return s + (cm?.capacity ?? 0);
  }, 0);
  // 目標Pt 合計は各行に表示している目標Pt (稼働日 × 期待値) をそのまま合算する。
  // 実績(backlog)の有無で絞らないため、未来Q (実績がまだ無い月) でもバーチャートの
  // 当月総目標と整合した合計が表示される。
  const totalTargetPt = members.reduce((s, m) => {
    const cm = m.months[currentMonthIdx];
    if (!cm) return s;
    const ep = expectedOf(m);
    return s + (cm.businessDays > 0 && ep !== null ? cm.businessDays * ep : 0);
  }, 0);
  // 各行の差分は (完了Pt + 処理中Pt) − 目標Pt なので、合計も処理中Ptを含めて
  // 計算しないと「各行の差分の合計」と「合計行の差分」が一致しない。
  const totalDiff = totalCompleted + totalInProgress - totalTargetPt;

  // 表示中の月が「現実時間でまだ到達していない未来月」かどうか。
  // 未来月は実績が出ようがないため、差分(マイナス)は表示せず "-" にする。
  const displayedMonthKey = (() => {
    for (const m of members) {
      const label = m.months[currentMonthIdx]?.month;
      if (label) {
        const key = monthHeaderToYyyymm(label);
        if (key) return key;
      }
    }
    return null;
  })();
  const isFutureMonth = displayedMonthKey !== null && displayedMonthKey > currentMonthKeyJst();

  return (
    <div className="flex-1 min-w-0">
      {accentColor ? (
        // ACE サブチーム別表示: 代表色の帯 (左ボーダー + 薄い背景) で色分けする
        <div
          className="mb-3 rounded-md px-3 py-1.5"
          style={{ backgroundColor: `${accentColor}1A`, borderLeft: `4px solid ${accentColor}` }}
        >
          <h3 className="text-base font-bold" style={{ color: accentColor }}>
            {teamLabel}
          </h3>
        </div>
      ) : (
        <div className="flex items-center justify-between mb-3 gap-2">
          <h3 className="text-lg font-bold text-[var(--color-text-primary)]">{teamLabel}</h3>
        </div>
      )}
      {members.length === 0 && emptyMessage ? (
        <p className="py-6 text-center text-xs text-[var(--color-text-muted)]">{emptyMessage}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b-2 border-[var(--color-border)]">
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)]">
                  {t('members.combatPower.name')}
                </th>
                {/* 稼働日は視認バランス調整で内容をやや左へ寄せる（右パディングを厚くする。列幅は不変） */}
                <th className="text-right py-2 pl-0 pr-4 font-medium text-[var(--color-text-muted)]">
                  {t('members.combatPower.businessDays')}
                </th>
                {/* 実績戦闘力 / 実績Pt は見出しが長いため括弧部分を 2 行目に落とす。
                    本文・括弧行とも右揃えで、数字列の右端に揃える。列幅は固定せず自動調整。 */}
                <th className="text-right py-2 px-1.5 font-medium text-[var(--color-text-muted)]">
                  <TwoLineHeaderLabel label={t('members.combatPower.actual')} />
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                  {t('members.combatPower.expected')}
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                  {t('members.combatPower.openPt')}
                </th>
                {/* 実績Pt は視認バランス調整で列全体をやや右へ寄せる（右パディングを削る）。列幅は固定しない */}
                <th className="text-right py-2 pl-3 pr-0 font-medium text-[var(--color-text-muted)]">
                  <TwoLineHeaderLabel label={t('members.combatPower.actualPt')} />
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)]">
                  {t('members.combatPower.targetPt')}
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)]">
                  {t('members.combatPower.diff')}
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const cm = m.months[currentMonthIdx];
                if (!cm) return null;
                const bp = cm.backlogPt;
                const completedPt = bp?.completed ?? null;
                const inProgressPt = bp?.inProgress ?? 0;
                const openPt = bp?.open ?? 0;
                const actualPt = completedPt !== null ? completedPt + inProgressPt : null;
                const ep = expectedOf(m);
                const targetPt = cm.businessDays > 0 && ep !== null ? cm.businessDays * ep : null;
                const diff = actualPt !== null && targetPt !== null ? actualPt - targetPt : null;

                const actualPowerDisplay = formatActualPowerDisplay(cm);

                const actualPtDisplay =
                  completedPt !== null ? formatPtWithWip(completedPt, inProgressPt) : '-';

                const memberEmail = nameToEmail?.get(m.name.toLowerCase());

                return (
                  <tr
                    key={m.name}
                    className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors"
                  >
                    <td className="py-1.5 px-2 font-medium">
                      {memberEmail ? (
                        <Link
                          to="/members/$email"
                          params={{ email: memberEmail }}
                          className="text-[var(--color-accent-primary)] hover:underline"
                        >
                          {m.name}
                        </Link>
                      ) : (
                        <span className="text-[var(--color-text-primary)]">{m.name}</span>
                      )}
                    </td>
                    <td className="py-1.5 pl-0 pr-4 text-right text-[var(--color-text-secondary)]">
                      {formatNum(cm.businessDays, 1)}
                    </td>
                    <td className="py-1.5 px-1.5 text-right font-medium text-[var(--color-text-primary)] whitespace-nowrap">
                      {actualPowerDisplay}
                    </td>
                    <td className="py-1.5 px-2 text-right text-[var(--color-text-secondary)]">
                      {ep !== null ? formatNum(ep) : '-'}
                    </td>
                    <td className="py-1.5 px-2 text-right text-[var(--color-text-secondary)]">
                      {openPt > 0 ? formatNum(openPt, 1) : '-'}
                    </td>
                    <td className="py-1.5 pl-3 pr-0 text-right text-[var(--color-text-secondary)] whitespace-nowrap">
                      {actualPtDisplay}
                    </td>
                    <td className="py-1.5 px-2 text-right text-[var(--color-text-secondary)] whitespace-nowrap">
                      {targetPt !== null ? formatNum(targetPt, 1) : '-'}
                      {cm.capacity !== null && (
                        <span className="ml-0.5 text-[10px] text-[var(--color-text-muted)]">
                          ({formatNum(cm.capacity, 1)})
                        </span>
                      )}
                    </td>
                    <td
                      className="py-1.5 px-2 text-right font-medium"
                      style={
                        diff !== null ? { color: diff >= 0 ? '#16a34a' : '#ef4444' } : undefined
                      }
                    >
                      {diff !== null ? `${diff >= 0 ? '+' : ''}${formatNum(diff, 1)}` : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[var(--color-border)] font-bold">
                <td className="py-2 px-2 text-[var(--color-text-primary)]">
                  {t('members.combatPower.teamTotal')}
                </td>
                <td className="py-2 pl-0 pr-4 text-right text-[var(--color-text-secondary)]">-</td>
                <td className="py-2 px-1.5 text-right text-[var(--color-text-primary)] whitespace-nowrap">
                  {(() => {
                    const withBacklog = members.filter((m) => {
                      const cm = m.months[currentMonthIdx];
                      return cm?.backlogPt && cm.businessDays > 0;
                    });
                    if (withBacklog.length === 0) {
                      const withActual = members.filter(
                        (m) => m.months[currentMonthIdx]?.actualPower !== null,
                      );
                      if (withActual.length === 0) return '-';
                      const avg =
                        withActual.reduce(
                          (s, m) => s + (m.months[currentMonthIdx]?.actualPower ?? 0),
                          0,
                        ) / withActual.length;
                      return formatNum(avg, 1);
                    }
                    let sumCompleted = 0;
                    let sumTotal = 0;
                    let hasWip = false;
                    for (const m of withBacklog) {
                      const cm = m.months[currentMonthIdx];
                      const bp = cm?.backlogPt;
                      if (!cm || !bp) continue;
                      sumCompleted += bp.completed / cm.businessDays;
                      sumTotal += (bp.completed + bp.inProgress) / cm.businessDays;
                      if (bp.inProgress > 0) hasWip = true;
                    }
                    const avgCompleted = sumCompleted / withBacklog.length;
                    const avgTotal = sumTotal / withBacklog.length;
                    return hasWip
                      ? `${formatNum(avgCompleted, 1)}(${formatNum(avgTotal, 1)})`
                      : formatNum(avgCompleted, 1);
                  })()}
                </td>
                <td className="py-2 px-2 text-right text-[var(--color-text-secondary)]">-</td>
                <td className="py-2 px-2 text-right text-[var(--color-text-secondary)]">
                  {totalOpen > 0 ? formatNum(totalOpen, 1) : '-'}
                </td>
                <td className="py-2 pl-3 pr-0 text-right text-[var(--color-text-secondary)] whitespace-nowrap">
                  {formatPtWithWip(totalCompleted, totalInProgress)}
                </td>
                <td className="py-2 px-2 text-right text-[var(--color-text-secondary)] whitespace-nowrap">
                  {formatNum(totalTargetPt, 1)}
                  {totalCapacity > 0 && (
                    <span className="ml-0.5 text-[10px] font-normal text-[var(--color-text-muted)]">
                      ({formatNum(totalCapacity, 1)})
                    </span>
                  )}
                </td>
                <td
                  className="py-2 px-2 text-right font-bold"
                  style={
                    isFutureMonth ? undefined : { color: totalDiff >= 0 ? '#16a34a' : '#ef4444' }
                  }
                >
                  {isFutureMonth ? '-' : `${totalDiff >= 0 ? '+' : ''}${formatNum(totalDiff, 1)}`}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
